/**
 * Thread — the scrollable conversation surface.
 *
 * Renders the conversation messages top-to-bottom (oldest at top,
 * newest at bottom — universal chat pattern). Auto-scrolls to the
 * newest message on every append.
 *
 * Visual contract (locked 2026-06-15):
 *   - 720px max-width, centered
 *   - 18px gap between turns (UserMessage / AgentMessage / chips block)
 *   - The chip stack belonging to an AgentMessage renders directly
 *     below it with the indented padding-left:44px that ChipsRow
 *     handles
 *
 * Day 4 (2026-06-15): pure render from props. AgentClient owns the
 * messages state and the typing flag. Day 6 wires the chip-click
 * navigation to /compare.
 */

"use client";

import { useEffect, useRef } from "react";

import { AgentMessage } from "./AgentMessage";
import { ChipsRow } from "./ChipsRow";
import { TypingIndicator } from "./TypingIndicator";
import { UserMessage } from "./UserMessage";
import type { Chip, ConversationMessage } from "./types";

export interface ThreadProps {
  messages: ConversationMessage[];
  /** When true, render TypingIndicator after the last message. */
  isLoading?: boolean;
  /** Click handler for chips. AgentClient wires this to navigate. */
  onChipClick: (chip: Chip) => void;
}

export function Thread({
  messages,
  isLoading = false,
  onChipClick,
}: ThreadProps) {
  // Auto-scroll to bottom on every change. scrollIntoView is supported
  // everywhere we care about; the smooth option falls back to "auto"
  // on reduced-motion preference automatically in modern browsers.
  const endRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, isLoading]);

  return (
    <div
      className="mx-auto flex w-full flex-col"
      style={{
        maxWidth: 720,
        padding: "0 24px",
        gap: 18,
      }}
      role="log"
      aria-live="polite"
      aria-busy={isLoading}
      aria-label="Conversation with Octopusden"
    >
      {messages.map((msg, idx) => {
        if (msg.role === "user") {
          return <UserMessage key={`u-${idx}`} content={msg.content} />;
        }
        // Assistant message: agent bubble + (if any) chips beneath.
        return (
          <div
            key={`a-${idx}-${msg.turn_id ?? ""}`}
            className="flex flex-col"
            style={{ gap: 0 }}
          >
            <AgentMessage
              acknowledgment={msg.acknowledgment}
              message={msg.message}
            />
            <ChipsRow chips={msg.chips} onChipClick={onChipClick} />
          </div>
        );
      })}
      {isLoading ? <TypingIndicator /> : null}
      <div ref={endRef} aria-hidden="true" style={{ height: 1 }} />
    </div>
  );
}
